/**
 * Vier kleine Knoepfe oben an der Guide-Spalte: nebeneinander, uebereinander, nur
 * Guide, nur Chat — dazu der Knopf fuer den zweiten Bildschirm (Rafael, 25.09.2026).
 */
import type { ReactNode } from 'react';
import type { GuideAnordnungModus } from '../../shared/guide-anordnung';

/** Kleines Schaubild: gefuellt = Guide, umrandet = Chat. Auch oben in der Leiste (ProjectTabs). */
export function Bild({ modus }: { modus: GuideAnordnungModus }) {
  const g = 'var(--tn-blue, var(--tnf-7aa2f7))';
  const c = 'currentColor';
  const teile: Record<GuideAnordnungModus, ReactNode> = {
    nebeneinander: <><rect x="1" y="1" width="7" height="10" fill={g} /><rect x="9.5" y="1.5" width="5" height="9" fill="none" stroke={c} /></>,
    uebereinander: <><rect x="1" y="1" width="14" height="5" fill={g} /><rect x="1.5" y="7.5" width="13" height="3" fill="none" stroke={c} /></>,
    nurGuide: <rect x="1" y="1" width="14" height="10" fill={g} />,
    nurChat: <rect x="1.5" y="1.5" width="13" height="9" fill="none" stroke={c} />,
  };
  return <svg width="16" height="12" viewBox="0 0 16 12" aria-hidden="true">{teile[modus]}</svg>;
}

// Der Standard (uebereinander) steht im Menue zuerst.
export const ANORDNUNG_MODI = ['uebereinander', 'nebeneinander', 'nurGuide', 'nurChat'] as const;

/*
 * „Chat" hiess hier bis 03.10.2026 der Kanzlerbereich (CoachSpalte + FremdCoachSpalte) —
 * Rafael 10:29: „das ist auch nicht wirklich klar, wo man das umschaltet". Gemeint sind
 * Kanzler (dieses Haus) und Vizekanzler (das andere Haus), nicht irgendein Chat.
 */
export const TITEL: Record<GuideAnordnungModus, string> = {
  nebeneinander: 'Kanzler rechts neben dem Guide',
  uebereinander: 'Kanzler unter dem Guide (Standard)',
  nurGuide: 'Nur Guide',
  nurChat: 'Nur Kanzler',
};

/** Kurzform fuer den Knopf „Ansicht ▾" — nur fuer Abweichungen vom Standard (uebereinander). */
export const ABWEICHUNG_KURZ: Partial<Record<GuideAnordnungModus, string>> = {
  nebeneinander: 'Kanzler rechts',
  nurGuide: 'ohne Kanzler',
  nurChat: 'nur Kanzler',
};

export type ZweiterZustand = 'aus' | 'wartet' | 'gekoppelt' | 'getrennt';

const KNOPF = { background: 'transparent', border: '1px solid transparent', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', color: 'var(--tn-text-muted)', display: 'inline-flex', alignItems: 'center' } as const;

/**
 * Der Knopf bzw. Zustand des zweiten Bildschirms. Seit 03.10.2026 steht er in der
 * Kopfleiste: ungekoppelt als Eintrag im ⋯-Menue (selten gebraucht), gekoppelt als
 * sichtbarer Zustand mit „Beenden" — ein Zustand gehoert nicht ins Menue.
 */
export function ZweiterBildschirm(props: {
  zweiter: ZweiterZustand;
  zweiterFehler: string | null;
  onZweiterOeffnen: () => void;
  onZweiterBeenden: () => void;
}) {
  const { zweiter } = props;
  const knopf = KNOPF;
  return <>
    {zweiter === 'aus'
        ? <button data-ai-id="guide-zweiter-bildschirm" onClick={props.onZweiterOeffnen} disabled={!!props.zweiterFehler}
            title={props.zweiterFehler ?? 'Öffnet ein zweites Fenster: dort geht jede Bühne, die du hier anklickst, im Vollbild auf.'}
            style={{ ...knopf, fontSize: 12 }}>
            ⧉ Zweiten Bildschirm öffnen
          </button>
        : <>
            <span data-ai-id="guide-zweiter-zustand" style={{ fontSize: 12, color: zweiter === 'gekoppelt' ? 'var(--tn-green, var(--tnf-9ece6a))' : 'var(--tn-warning, var(--tnf-e0af68))' }}>
              {zweiter === 'gekoppelt' ? '⧉ Bildschirm 2 verbunden' : zweiter === 'wartet' ? '⧉ Bildschirm 2 wird verbunden …' : '⧉ Bildschirm 2 getrennt'}
            </span>
            {zweiter === 'getrennt' && <button data-ai-id="guide-zweiter-neu" onClick={props.onZweiterOeffnen} style={{ ...knopf, fontSize: 12 }}>Neu öffnen</button>}
            <button data-ai-id="guide-zweiter-beenden" onClick={props.onZweiterBeenden} style={{ ...knopf, fontSize: 12 }} title="Kopplung beenden; Bühnen gehen wieder hier auf.">Beenden</button>
          </>}
  </>;
}

export default function GuideAnordnungSchalter(props: {
  modus: GuideAnordnungModus;
  onModus: (m: GuideAnordnungModus) => void;
  /** Solange Bildschirm 2 gekoppelt ist, gilt „nebeneinander" — die Wahl bleibt gemerkt, ist aber nicht wirksam. */
  festgelegt: boolean;
  zweiter: ZweiterZustand;
  zweiterFehler: string | null;
  onZweiterOeffnen: () => void;
  onZweiterBeenden: () => void;
}) {
  const { modus, onModus, festgelegt, zweiter } = props;
  const knopf = { background: 'transparent', border: '1px solid transparent', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', color: 'var(--tn-text-muted)', display: 'inline-flex', alignItems: 'center' } as const;
  return (
    <div role="group" aria-label="Wo der Kanzler steht" style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {ANORDNUNG_MODI.map(m => (
        <button key={m} data-ai-id={`guide-anordnung-${m}`} aria-pressed={modus === m} disabled={festgelegt}
          title={festgelegt ? 'Mit zweitem Bildschirm steht der Kanzler rechts neben dem Guide.' : TITEL[m]}
          aria-label={TITEL[m]} onClick={() => onModus(m)}
          style={{ ...knopf, ...(modus === m ? { borderColor: 'var(--tn-blue, var(--tnf-7aa2f7))', color: 'var(--tn-text)' } : {}), opacity: festgelegt ? 0.5 : 1 }}>
          <Bild modus={m} />
        </button>
      ))}
      <span style={{ width: 8 }} />
      <ZweiterBildschirm zweiter={zweiter} zweiterFehler={props.zweiterFehler} onZweiterOeffnen={props.onZweiterOeffnen} onZweiterBeenden={props.onZweiterBeenden} />
    </div>
  );
}
